import {ProductJson, ProductModel} from "../../models/product";
import {NextPage} from "next";
import {ProductDetailView} from "../../components/product/ProductDetailView";
import {useRouter} from "next/router";
import Bugsnag from "@bugsnag/js";
import {wrapper} from "../../app/store";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import React, {useEffect} from "react";
import {useAppDispatch} from "../../app/hooks";
import {fetchCart} from "../../redux/cart/cartSlice";
import {reset} from "../../redux/product/productSlice";
import Head from "next/head";
import {ImageUtils} from "../../utils/imageUtils";
import {MetaTag} from "../../models/metaTag";
import {Nutrient} from "../../models/nutrient";

interface ProductProps {
  id: string
  productJson?: ProductJson
}

const Product: NextPage<ProductProps> = ({ id, productJson }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const storeId = productJson?.store_id
  const product = ProductModel.fromJson(productJson)

  useEffect(() => {
    if (storeId) {
      dispatch(fetchCart(storeId))
    }
  }, [storeId, dispatch])

  return <>
    <ProductDetailView
      productId={id}
      product={product}
      onClose={async () => {
        if (productJson?.store_id) {
          await router.push(`/store/${productJson.store_id}`)
        } else {
          await router.push(`/store-feed`)
        }

        dispatch(reset())
      }}
    />
  </>
}

export async function getStaticPaths() {
  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-available-menu-items`).catch(e => {
    Bugsnag.notify(e)
    return undefined
  })
  const products: ProductJson[] = (await res?.json()) ?? []

  const paths = products.map((product) => ({
    params: {
      id: product._id,
      productJson: product
    }
  }))

  return { paths, fallback: true }
}

const createMetaTags = (params: any, productJson?: ProductJson): MetaTag[] => {
  const product = ProductModel.fromJson(productJson)
  const metaTitle = product?.name ?? null //(product && `${product?.name} | ${product?.vendorName} | Order on FitGenie`) ?? null
  let metaDescription = product?.description ?? ""
  const metaImage = ImageUtils.createUrlFromImage(product?.image) ?? null
  const metaUrl = `${process.env.NEXT_PUBLIC_APP_DOMAIN_URL}/product/${params.id ?? product?.id}`
  const sku = product?.getSelectedSku()

  const nutrients = sku?.deliveredMeal?.getNutrients()
  const calories = nutrients?.get('CALORIES')?.toString()
  const protein = nutrients?.get('PROTEIN')?.toString()
  const carbs = nutrients?.get('CARBOHYDRATE')?.toString()
  const fat = nutrients?.get('FAT')?.toString()

  const caloriesLabel = calories && `Calories: ${calories}`;
  const proteinLabel = protein && `Protein: ${protein}g`;
  const carbsLabel = carbs && `Carbs: ${carbs}g`;
  const fatLabel = fat && `Fat: ${fat}g`;

  if (caloriesLabel || proteinLabel || carbsLabel || fatLabel) {
    metaDescription += '\n'
  }

   metaDescription += caloriesLabel ? `\n${caloriesLabel}` : ''
   metaDescription += proteinLabel ? `\n${proteinLabel}` : ''
   metaDescription += carbsLabel ? `\n${carbsLabel}` : ''
   metaDescription += fatLabel ? `\n${fatLabel}` : ''

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return [
    {
      property: 'og:title',
      content: metaTitle
    },
    {
      property: 'og:description',
      content: metaDescription
    },
    {
      property: 'og:image',
      content: metaImage
    },
    {
      property: 'og:url',
      content: metaUrl
    },
    {
      property: 'og:locale',
      content: 'en_US'
    },
    {
      property: 'og:price:amount',
      content: sku?.price ? formatter.format(sku.price) : null
    },
    {
      property: 'og:price:currency',
      content: sku?.currencyCode ?? null
    },
    {
      property: 'product:availability',
      content: 'in stock'
    },
    {
      property: 'product:brand',
      content: product?.vendorName ?? null
    },
    {
      property: 'product:catalog_id',
      content: sku?.id ?? null
    },
    {
      property: 'product:condition',
      content: 'new'
    },
    {
      property: 'product:custom_label_0',
      content: caloriesLabel ?? null
    },
    {
      property: 'product:custom_label_1',
      content: proteinLabel ?? null
    },
    {
      property: 'product:custom_label_2',
      content: carbsLabel ?? null
    },
    {
      property: 'product:custom_label_3',
      content: fatLabel ?? null
    },
    {
      property: 'product:item_group_id',
      content: product?.id ?? null
    },
    {
      property: 'product:gtin',
      content: sku?.barcode ?? null
    },
    {
      property: 'product:locale',
      content: 'en_US'
    },
    {
      property: 'product:category',
      content: '5814'
    },
    {
      property: 'product:price:amount',
      content: sku?.price ? formatter.format(sku.price) : null
    },
    {
      property: 'product:price:currency',
      content: sku?.currencyCode ?? null
    },
    {
      property: 'product:retailer_item_id',
      content: product?.id ?? null
    },
    {
      property: 'twitter:title',
      content: metaTitle
    },
    {
      property: 'twitter:description',
      content: metaDescription
    },
    {
      property: 'twitter:image',
      content: metaImage
    },
    {
      property: 'twitter:card',
      content: 'summary'
    },
  ]
}

export const getStaticProps = wrapper.getStaticProps(store => async ({ locale, params }: any) => {
  return  {
    notFound: true
  }
  // const translations = await serverSideTranslations(locale, ['common', 'landing', 'auth', 'location', 'store', 'payment', 'checkout', 'order'])
  //   .catch(e => {
  //     Bugsnag.notify(e)
  //   })
  //
  // if (params && params.id && params.productJson) {
  //   return {
  //     props: {
  //       id: params.id,
  //       metaTags: createMetaTags(params, params.productJson),
  //       productJson: params.productJson,
  //       ...translations
  //     }
  //   }
  // }
  //
  // const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-menu-item/${params.id}`).catch(e => {
  //   Bugsnag.notify(e)
  //   return undefined
  // })
  //
  // const productJson: ProductJson | undefined = await res?.json()
  //
  //
  // return {
  //   props: {
  //     id: productJson?._id,
  //     productJson,
  //     metaTags: createMetaTags(params, productJson),
  //     ...translations,
  //   }
  // }
});


export default Product